/**
 * IdVerifyGate — Wraps the payment card. If the current guest hasn't verified
 * their ID yet, shows an inline upload card. On success, unlocks the wrapped
 * content (proceed to pay).
 *
 * SBT-only incentives — no discount language (getbitsy doesn't control pricing).
 */
import { useEffect, useState, useCallback } from 'react';
import { Loader2, ShieldCheck, Upload, Sparkles, Check, X, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export function IdVerifyGate({
  email,
  bookingName,
  checkInIso,
  hotelId,
  hotelMinAge = 18,
  walletAddress,
  children,
}) {
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [imageB64, setImageB64] = useState(null);
  const [result, setResult] = useState(null);

  const checkStatus = useCallback(async () => {
    if (!email) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/id-verify/status/${encodeURIComponent(email)}`);
      const d = await r.json();
      // Gate passes only if: age_verified AND bucket >= hotelMinAge
      const passesAge =
        !!d.age_verified &&
        Number(d.min_age_met_bucket || 0) >= Number(hotelMinAge || 0);
      setVerified(passesAge);
    } catch {
      setVerified(false);
    } finally {
      setLoading(false);
    }
  }, [email, hotelMinAge]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setPreview(dataUrl);
      // strip data:...;base64,
      setImageB64(String(dataUrl).split(',')[1] || '');
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!imageB64) { toast.error('Choose an ID photo first'); return; }
    if (!bookingName?.trim()) { toast.error('Add your name to the booking first'); return; }
    setUploading(true);
    setResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/id-verify/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: email,
          image_base64: imageB64,
          booking_name: bookingName,
          booking_check_in_iso: checkInIso,
          hotel_id: hotelId,
          wallet_address: walletAddress,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Verification failed');
      setResult(d);
      if (d.success && d.age_valid && (d.min_age_met_bucket >= hotelMinAge)) {
        toast.success('ID verified · Your SBT is now Trusted Guest tier');
        // Clear image from component state immediately (privacy-first even on client)
        setImageB64(null);
        setPreview(null);
        setVerified(true);
      } else {
        toast.error(d.age_valid ? 'Age does not meet this hotel\'s requirement' : 'Could not verify age');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Still checking status ──
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="id-gate-loading">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  // ── Verified → show children (payment card) with small badge ──
  if (verified) {
    return (
      <>
        <div className="mb-3 flex items-center gap-2 text-xs" data-testid="id-gate-verified">
          <Badge className="bg-emerald-500/10 text-emerald-600 border border-emerald-500/30">
            <ShieldCheck className="w-3 h-3 mr-1" /> ID verified · Trusted Guest SBT
          </Badge>
          <span className="text-muted-foreground">Skip this step on every future booking</span>
        </div>
        {children}
      </>
    );
  }

  // ── Not verified → inline upload card ──
  return (
    <div
      className="rounded-xl border-2 border-primary/30 bg-card p-5 shadow-lg"
      data-testid="id-gate-upload"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">One step before you book</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            This hotel requires age verification (min {hotelMinAge}+).
          </p>
        </div>
      </div>

      {/* SBT benefits (no discount language) */}
      <div className="bg-muted/40 rounded-lg p-3 mb-4 space-y-1.5">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
          Verify once, use everywhere
        </p>
        <Benefit icon={<Sparkles className="w-3 h-3 text-primary" />}
                 text="Skip ID upload at every future getbitsy hotel" />
        <Benefit icon={<ShieldCheck className="w-3 h-3 text-emerald-500" />}
                 text="Trusted Guest badge on your SBT (reputation that travels)" />
        <Benefit icon={<Check className="w-3 h-3 text-cyan-500" />}
                 text="Faster front-desk check-in — staff just does a visual match" />
      </div>

      {/* Privacy note */}
      <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3 mb-4 flex gap-2" data-testid="id-gate-privacy">
        <Info className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" />
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          We check your age, compare the name on your ID to your booking, then <strong>delete the photo</strong>.
          We <strong>never store your DOB, ID number, or image</strong> — only boolean flags on your SBT.
        </p>
      </div>

      {/* Upload control */}
      {!preview ? (
        <label
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 rounded-lg p-6 cursor-pointer transition"
          data-testid="id-gate-upload-label"
        >
          <Upload className="w-6 h-6 text-muted-foreground" />
          <p className="text-sm font-medium">Upload passport / driver's license / national ID</p>
          <p className="text-[11px] text-muted-foreground">JPEG or PNG · max 8 MB</p>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onFile}
            className="hidden"
            data-testid="id-gate-file-input"
          />
        </label>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <img
              src={preview}
              alt="ID preview"
              className="w-full max-h-48 object-contain rounded-lg border"
              data-testid="id-gate-preview"
            />
            <button
              onClick={() => { setPreview(null); setImageB64(null); }}
              className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
              data-testid="id-gate-clear"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <Button
            onClick={submit}
            disabled={uploading}
            className="w-full h-11"
            data-testid="id-gate-submit"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying…</>
            ) : (
              <><ShieldCheck className="w-4 h-4 mr-2" /> Verify & continue</>
            )}
          </Button>
        </div>
      )}

      {result && !result.age_valid && (
        <div className="mt-3 rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-600" data-testid="id-gate-fail">
          Could not verify. Please try a clearer photo where the date of birth is readable.
        </div>
      )}

      <p className="text-[10px] text-muted-foreground text-center mt-4">
        By uploading, you consent to a one-time age check. See <a href="/privacy" className="underline">Privacy Policy</a>.
      </p>
    </div>
  );
}

function Benefit({ icon, text }) {
  return (
    <div className="flex items-center gap-2 text-[12px]">
      {icon}
      <span>{text}</span>
    </div>
  );
}

export default IdVerifyGate;
